import {gql} from '@apollo/client';
import React from 'react';

import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import notifications from 'notifications';
import {ProcessTaskSchemaForCreate} from 'processes/CreateProcessUtil';
import {handleSelectSchema} from 'processes/tables/column-mapping/handleSelectSchema';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Menu from 'ui-library/menus/Menu';
import {OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {withLoadingScreen} from 'util/Promises';

import {
  GetCreateProcessTasksOptionQuery,
  GetCreateProcessTasksOptionQueryVariables,
} from './__generated/GetCreateProcessTasksOptionQuery';
import getProcessTaskColumnMappingInfoFromQuery from './getProcessTaskColumnMappingInfoFromQuery';

export const GET_CREATE_PROCESS_TASKS_OPTION_QUERY = gql`
  query GetCreateProcessTasksOptionQuery($id: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $id) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
`;

function getOptionsForCreate(
  processTaskSchemas: Array<ProcessTaskSchemaForCreate>,
  runQueryState: RunQueryState,
  selectorsById: SchemaFieldSelectorsDict
): JSX.Element {
  const sourceColumns = getProcessTaskColumnMappingInfoFromQuery(runQueryState);

  return (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
      <Section>
        <SectionHeader>Workflow task schemas</SectionHeader>
        {processTaskSchemas.length ? (
          processTaskSchemas.map((schema) => (
            <Option
              key={schema.id}
              contentType={OptionContentType.PLAIN}
              onSelect={async () => {
                const result = await withLoadingScreen(
                  doQuerySafe<GetCreateProcessTasksOptionQuery, GetCreateProcessTasksOptionQueryVariables>(
                    {query: GET_CREATE_PROCESS_TASKS_OPTION_QUERY},
                    {
                      variables: {id: schema.id},
                    }
                  )
                );
                if (!isInaccessible(result.processTaskSchema)) {
                  await handleSelectSchema(
                    sourceColumns,
                    result.processTaskSchema,
                    selectorsById,
                    'insights',
                    ActionOrigin.INSIGHTS
                  );
                } else {
                  notifications.error('Selected task schema is not accessible');
                }
              }}
              shouldEllipsize={false}
              value={schema.name}
            >
              {schema.name}
            </Option>
          ))
        ) : (
          <Option contentType={OptionContentType.PLAIN} isDisabled={true}>
            No workflow task schemas found
          </Option>
        )}
      </Section>
    </Menu>
  );
}

export default function getCreateProcessTasksOption(props: {
  runQueryState: RunQueryState;
  processTaskSchemas: Array<ProcessTaskSchemaForCreate> | null;
  selectorsById: SchemaFieldSelectorsDict;
}): JSX.Element {
  return (
    <Option icon="process-task" isDisabled={props.runQueryState.status !== AsyncStatus.SUCCESS}>
      Create workflow tasks
      {props.runQueryState.status === AsyncStatus.SUCCESS &&
        props.processTaskSchemas != null &&
        getOptionsForCreate(props.processTaskSchemas, props.runQueryState, props.selectorsById)}
    </Option>
  );
}
